import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { searchFlights, searchAirports } from './services/flightApiService';
import './DuffelTestPage.css';

const DuffelTestPage = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('search');
  const [searchParams, setSearchParams] = useState({
    origin: 'LOS',
    destination: 'LHR',
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    cabinClass: 'economy'
  });

  const [airportSearch, setAirportSearch] = useState({
    query: ''
  });

  const [loading, setLoading] = useState(false);
  const [flights, setFlights] = useState([]);
  const [airports, setAirports] = useState([]);
  const [error, setError] = useState(null);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [showPassengerForm, setShowPassengerForm] = useState(false);
  const [passengerData, setPassengerData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '+2348012345678',
    dateOfBirth: '1990-01-15',
    title: 'mr',
    gender: 'male',
    nationality: 'NG'
  });

  // Set default date to tomorrow
  React.useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setSearchParams(prev => ({
      ...prev,
      departureDate: dateStr
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAirportSearchChange = (e) => {
    setAirportSearch({
      query: e.target.value
    });
  };

  const handleSearchFlights = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFlights([]);
    setSearchExecuted(false);
    setSelectedFlight(null);
    setApiStats(null);

    const startTime = Date.now();

    try {
      const result = await searchFlights(searchParams);
      const endTime = Date.now();
      
      setApiStats({
        responseTime: endTime - startTime,
        timestamp: new Date().toISOString()
      });
      
      if (result.success) {
        setFlights(result.data);
        setSearchExecuted(true);
      } else {
        setError(result.error || 'Failed to search flights');
        console.error('Search error:', result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      console.error('Search exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAirports = async (e) => {
    e.preventDefault();
    if (!airportSearch.query || airportSearch.query.length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setAirports([]);

    try {
      const result = await searchAirports(airportSearch.query);
      
      if (result.success) {
        setAirports(result.data);
      } else {
        setError(result.error || 'Failed to search airports');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    };
  };

  return (
    <div className="duffel-test-page">
      <Header />
      
      <div className="test-page-container">
        <div className="test-page-header">
          <h1>🚀 Duffel API Test Page</h1>
          <p>Comprehensive testing for all Duffel API features</p>
        </div>

        {/* Tabs */}
        <div className="test-tabs">
          <button 
            className={activeTab === 'search' ? 'active' : ''}
            onClick={() => {
              setActiveTab('search');
              setShowPassengerForm(false);
            }}
          >
            Flight Search
          </button>
          <button 
            className={activeTab === 'airports' ? 'active' : ''}
            onClick={() => {
              setActiveTab('airports');
              setShowPassengerForm(false);
            }}
          >
            Airport Search
          </button>
          <button 
            className={activeTab === 'passenger' ? 'active' : ''}
            onClick={() => setActiveTab('passenger')}
            disabled={!selectedFlight}
          >
            Passenger Details
          </button>
          <button 
            className={activeTab === 'details' ? 'active' : ''}
            onClick={() => {
              setActiveTab('details');
              setShowPassengerForm(false);
            }}
            disabled={!selectedFlight}
          >
            Flight Details
          </button>
        </div>

        {/* Flight Search Tab */}
        {activeTab === 'search' && (
          <>
            <div className="test-search-form">
              <form onSubmit={handleSearchFlights}>
                <div className="form-row">
                  <div className="form-group">
                    <label>From (Airport Code)</label>
                    <input
                      type="text"
                      name="origin"
                      value={searchParams.origin}
                      onChange={handleInputChange}
                      placeholder="e.g., LOS"
                      required
                      maxLength="3"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>To (Airport Code)</label>
                    <input
                      type="text"
                      name="destination"
                      value={searchParams.destination}
                      onChange={handleInputChange}
                      placeholder="e.g., LHR"
                      required
                      maxLength="3"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Departure Date</label>
                    <input
                      type="date"
                      name="departureDate"
                      value={searchParams.departureDate}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="form-group">
                    <label>Return Date (Optional)</label>
                    <input
                      type="date"
                      name="returnDate"
                      value={searchParams.returnDate}
                      onChange={handleInputChange}
                      min={searchParams.departureDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Adults</label>
                    <input
                      type="number"
                      name="adults"
                      value={searchParams.adults}
                      onChange={handleInputChange}
                      min="1"
                      max="9"
                    />
                  </div>

                  <div className="form-group">
                    <label>Children (2-11)</label>
                    <input
                      type="number"
                      name="children"
                      value={searchParams.children}
                      onChange={handleInputChange}
                      min="0"
                      max="9"
                    />
                  </div>

                  <div className="form-group">
                    <label>Infants (0-2)</label>
                    <input
                      type="number"
                      name="infants"
                      value={searchParams.infants}
                      onChange={handleInputChange}
                      min="0"
                      max="9"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cabin Class</label>
                    <select
                      name="cabinClass"
                      value={searchParams.cabinClass}
                      onChange={handleInputChange}
                    >
                      <option value="economy">Economy</option>
                      <option value="premium_economy">Premium Economy</option>
                      <option value="business">Business</option>
                      <option value="first">First</option>
                    </select>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="search-button"
                  disabled={loading}
                >
                  {loading ? 'Searching...' : '🔍 Search Flights'}
                </button>
              </form>
            </div>

            {/* API Stats */}
            {apiStats && (
              <div className="api-stats">
                <h3>API Performance</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <strong>Response Time:</strong> {apiStats.responseTime}ms
                  </div>
                  <div className="stat-item">
                    <strong>Timestamp:</strong> {new Date(apiStats.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <h3>❌ Error</h3>
                <p>{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Searching for flights with Duffel API...</p>
                <p className="loading-note">This may take a few seconds as Duffel generates offers asynchronously</p>
              </div>
            )}

            {/* Results */}
            {searchExecuted && !loading && (
              <div className="results-section">
                <h2>
                  {flights.length > 0 
                    ? `✅ Found ${flights.length} Flight${flights.length !== 1 ? 's' : ''}`
                    : '⚠️ No Flights Found'
                  }
                </h2>

                {flights.length > 0 ? (
                  <div className="flights-list">
                    {flights.map((flight, index) => (
                      <div key={flight.id || index} className="flight-card">
                        <div className="flight-header">
                          <div className="flight-airline">
                            <span className="airline-logo">✈️</span>
                            <span className="airline-name">{flight.airline}</span>
                            <span className="flight-number">{flight.flightNumber}</span>
                          </div>
                          <div className="flight-price">
                            {formatPrice(flight.price.total)}
                          </div>
                        </div>

                        <div className="flight-route">
                          <div className="route-segment">
                            <div className="route-time">
                              <span className="time">{flight.departureTime}</span>
                              <span className="airport">{flight.departureAirport}</span>
                            </div>
                            <div className="route-info">
                              <span className="duration">{flight.durationFormatted}</span>
                              <div className="route-line">
                                <span className="dot"></span>
                                <span className="line"></span>
                                <span className="dot"></span>
                              </div>
                              <span className="stops">
                                {flight.stops === 0 ? 'Non-stop' : `${flight.stops} Stop${flight.stops !== 1 ? 's' : ''}`}
                              </span>
                            </div>
                            <div className="route-time">
                              <span className="time">{flight.arrivalTime}</span>
                              <span className="airport">{flight.arrivalAirport}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flight-details">
                          <div className="detail-item">
                            <strong>Base Fare:</strong> {formatPrice(flight.price.base)}
                          </div>
                          <div className="detail-item">
                            <strong>Taxes:</strong> {formatPrice(flight.price.taxes)}
                          </div>
                          <div className="detail-item">
                            <strong>Currency:</strong> {flight.price.currency}
                          </div>
                          <div className="detail-item">
                            <strong>Available Seats:</strong> {flight.numberOfBookableSeats || 'N/A'}
                          </div>
                          <div className="detail-item">
                            <strong>Validating Airline:</strong> {flight.validatingAirlineCodes?.join(', ') || 'N/A'}
                          </div>
                          <div className="detail-item">
                            <strong>One Way:</strong> {flight.oneWay ? 'Yes' : 'No'}
                          </div>
                        </div>

                        {flight.outbound.segments.length > 1 && (
                          <div className="flight-segments">
                            <strong>Outbound Segments:</strong>
                            {flight.outbound.segments.map((segment, idx) => (
                              <div key={idx} className="segment">
                                <span className="segment-route">
                                  {segment.departure.airport} → {segment.arrival.airport}
                                </span>
                                <span className="segment-flight">
                                  {segment.carrier}{segment.flightNumber}
                                </span>
                                <span className="segment-time">
                                  {formatDateTime(segment.departure.time).time} - {formatDateTime(segment.arrival.time).time}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {flight.return && (
                          <div className="return-flight">
                            <strong>Return Flight:</strong>
                            <div className="route-segment">
                              <div className="route-time">
                                <span className="time">
                                  {formatDateTime(flight.return.segments[0].departure.time).time}
                                </span>
                                <span className="airport">{flight.return.segments[0].departure.airport}</span>
                              </div>
                              <div className="route-info">
                                <span className="duration">{flight.return.duration}</span>
                              </div>
                              <div className="route-time">
                                <span className="time">
                                  {formatDateTime(flight.return.segments[flight.return.segments.length - 1].arrival.time).time}
                                </span>
                                <span className="airport">{flight.return.segments[flight.return.segments.length - 1].arrival.airport}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flight-actions">
                          <button 
                            className="view-details-btn"
                            onClick={() => {
                              setSelectedFlight(flight);
                              setActiveTab('details');
                            }}
                          >
                            View Full Details
                          </button>
                          <button 
                            className="proceed-btn"
                            onClick={() => {
                              setSelectedFlight(flight);
                              setActiveTab('passenger');
                              setShowPassengerForm(true);
                            }}
                          >
                            ✈️ Proceed to Book
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-flights">
                    <p>No flights found for your search criteria. Try different dates or airports.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Airport Search Tab */}
        {activeTab === 'airports' && (
          <>
            <div className="test-search-form">
              <form onSubmit={handleSearchAirports}>
                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Search Airports/Cities</label>
                    <input
                      type="text"
                      value={airportSearch.query}
                      onChange={handleAirportSearchChange}
                      placeholder="e.g., Lagos, London, New York"
                      required
                      minLength="2"
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="search-button"
                  disabled={loading || !airportSearch.query || airportSearch.query.length < 2}
                >
                  {loading ? 'Searching...' : '🔍 Search Airports'}
                </button>
              </form>
            </div>

            {error && (
              <div className="error-message">
                <h3>❌ Error</h3>
                <p>{error}</p>
              </div>
            )}

            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Searching for airports...</p>
              </div>
            )}

            {airports.length > 0 && (
              <div className="results-section">
                <h2>Found {airports.length} Airport(s)/City(ies)</h2>
                <div className="airports-list">
                  {airports.map((airport, index) => (
                    <div key={airport.iataCode || index} className="airport-card">
                      <div className="airport-header">
                        <h3>{airport.name}</h3>
                        <span className="airport-code">{airport.iataCode}</span>
                      </div>
                      <div className="airport-details">
                        <div className="detail-item">
                          <strong>Type:</strong> {airport.type || airport.subType || 'N/A'}
                        </div>
                        {airport.address && (
                          <>
                            <div className="detail-item">
                              <strong>City:</strong> {airport.address.cityName || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Country:</strong> {airport.address.countryCode || 'N/A'}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Passenger Details Form Tab */}
        {activeTab === 'passenger' && selectedFlight && (
          <div className="passenger-form-section">
            <button 
              className="back-button"
              onClick={() => setActiveTab('search')}
            >
              ← Back to Results
            </button>

            <div className="selected-flight-summary">
              <h3>Selected Flight</h3>
              <div className="flight-summary-card">
                <div className="summary-row">
                  <span><strong>Airline:</strong> {selectedFlight.airline}</span>
                  <span><strong>Flight:</strong> {selectedFlight.flightNumber}</span>
                </div>
                <div className="summary-row">
                  <span><strong>Route:</strong> {selectedFlight.departureAirport} → {selectedFlight.arrivalAirport}</span>
                  <span><strong>Price:</strong> {formatPrice(selectedFlight.price.total)}</span>
                </div>
                <div className="summary-row">
                  <span><strong>Departure:</strong> {selectedFlight.departureTime}</span>
                  <span><strong>Arrival:</strong> {selectedFlight.arrivalTime}</span>
                </div>
              </div>
            </div>

            <div className="passenger-form-card">
              <h2>Passenger Details Form</h2>
              <p className="form-note">📝 Form is pre-filled with dummy data for testing</p>
              
              <form className="passenger-form" onSubmit={(e) => {
                e.preventDefault();
                alert('Passenger data collected! Ready for booking.\n\n' + JSON.stringify(passengerData, null, 2));
              }}>
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Title <span className="required">*</span></label>
                      <select
                        name="title"
                        value={passengerData.title}
                        onChange={(e) => setPassengerData({...passengerData, title: e.target.value})}
                        className="form-input"
                        required
                      >
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="ms">Ms</option>
                        <option value="miss">Miss</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>First Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="firstName"
                        value={passengerData.firstName}
                        onChange={(e) => setPassengerData({...passengerData, firstName: e.target.value})}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Last Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="lastName"
                        value={passengerData.lastName}
                        onChange={(e) => setPassengerData({...passengerData, lastName: e.target.value})}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date of Birth <span className="required">*</span></label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={passengerData.dateOfBirth}
                        onChange={(e) => setPassengerData({...passengerData, dateOfBirth: e.target.value})}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={passengerData.gender}
                        onChange={(e) => setPassengerData({...passengerData, gender: e.target.value})}
                        className="form-input"
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Nationality</label>
                      <input
                        type="text"
                        name="nationality"
                        value={passengerData.nationality}
                        onChange={(e) => setPassengerData({...passengerData, nationality: e.target.value})}
                        className="form-input"
                        placeholder="e.g., NG, US, GB"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Contact Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email Address <span className="required">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={passengerData.email}
                        onChange={(e) => setPassengerData({...passengerData, email: e.target.value})}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Phone Number <span className="required">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={passengerData.phone}
                        onChange={(e) => setPassengerData({...passengerData, phone: e.target.value})}
                        className="form-input"
                        placeholder="+2348012345678"
                        required
                      />
                      <small className="form-hint">Include country code (e.g., +234)</small>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Booking Information</h3>
                  <div className="info-box">
                    <p><strong>Offer ID:</strong> {selectedFlight.id}</p>
                    <p><strong>Total Price:</strong> {formatPrice(selectedFlight.price.total)}</p>
                    <p><strong>Currency:</strong> {selectedFlight.price.currency}</p>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setActiveTab('search')}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    ✅ Submit Passenger Details
                  </button>
                </div>
              </form>

              <div className="form-preview">
                <details>
                  <summary>📋 Preview Passenger Data (for Duffel API)</summary>
                  <pre>{JSON.stringify({
                    type: 'adult',
                    given_name: passengerData.firstName,
                    family_name: passengerData.lastName,
                    email: passengerData.email,
                    phone_number: passengerData.phone,
                    born_on: passengerData.dateOfBirth,
                    title: passengerData.title,
                    gender: passengerData.gender
                  }, null, 2)}</pre>
                </details>
              </div>
            </div>
          </div>
        )}

        {/* Flight Details Tab */}
        {activeTab === 'details' && selectedFlight && (
          <div className="flight-details-page">
            <button 
              className="back-button"
              onClick={() => setActiveTab('search')}
            >
              ← Back to Results
            </button>
            
            <h2>Full Flight Details</h2>
            
            <div className="details-sections">
              <div className="details-section">
                <h3>Basic Information</h3>
                <div className="details-grid">
                  <div><strong>Offer ID:</strong> {selectedFlight.id}</div>
                  <div><strong>Airline:</strong> {selectedFlight.airline}</div>
                  <div><strong>Flight Number:</strong> {selectedFlight.flightNumber}</div>
                  <div><strong>One Way:</strong> {selectedFlight.oneWay ? 'Yes' : 'No'}</div>
                </div>
              </div>

              <div className="details-section">
                <h3>Pricing</h3>
                <div className="details-grid">
                  <div><strong>Total:</strong> {formatPrice(selectedFlight.price.total)}</div>
                  <div><strong>Base Fare:</strong> {formatPrice(selectedFlight.price.base)}</div>
                  <div><strong>Taxes:</strong> {formatPrice(selectedFlight.price.taxes)}</div>
                  <div><strong>Currency:</strong> {selectedFlight.price.currency}</div>
                </div>
              </div>

              <div className="details-section">
                <h3>Outbound Flight</h3>
                <div className="segments-detail">
                  {selectedFlight.outbound.segments.map((segment, idx) => (
                    <div key={idx} className="segment-detail">
                      <div className="segment-header">
                        <span><strong>Segment {idx + 1}</strong></span>
                        <span>{segment.carrier}{segment.flightNumber}</span>
                      </div>
                      <div className="segment-info">
                        <div>
                          <strong>Departure:</strong> {segment.departure.airport} 
                          <br />
                          {formatDateTime(segment.departure.time).date} at {formatDateTime(segment.departure.time).time}
                          {segment.departure.terminal && ` (Terminal ${segment.departure.terminal})`}
                        </div>
                        <div>
                          <strong>Arrival:</strong> {segment.arrival.airport}
                          <br />
                          {formatDateTime(segment.arrival.time).date} at {formatDateTime(segment.arrival.time).time}
                          {segment.arrival.terminal && ` (Terminal ${segment.arrival.terminal})`}
                        </div>
                        {segment.aircraft && (
                          <div><strong>Aircraft:</strong> {segment.aircraft}</div>
                        )}
                        <div><strong>Duration:</strong> {segment.duration}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedFlight.return && (
                <div className="details-section">
                  <h3>Return Flight</h3>
                  <div className="segments-detail">
                    {selectedFlight.return.segments.map((segment, idx) => (
                      <div key={idx} className="segment-detail">
                        <div className="segment-header">
                          <span><strong>Segment {idx + 1}</strong></span>
                          <span>{segment.carrier}{segment.flightNumber}</span>
                        </div>
                        <div className="segment-info">
                          <div>
                            <strong>Departure:</strong> {segment.departure.airport}
                            <br />
                            {formatDateTime(segment.departure.time).date} at {formatDateTime(segment.departure.time).time}
                          </div>
                          <div>
                            <strong>Arrival:</strong> {segment.arrival.airport}
                            <br />
                            {formatDateTime(segment.arrival.time).date} at {formatDateTime(segment.arrival.time).time}
                          </div>
                          {segment.aircraft && (
                            <div><strong>Aircraft:</strong> {segment.aircraft}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="details-section">
                <h3>Raw API Response</h3>
                <details>
                  <summary>View Raw JSON Data</summary>
                  <pre>{JSON.stringify(selectedFlight.rawOffer || selectedFlight, null, 2)}</pre>
                </details>
              </div>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <h3>📋 Test Information</h3>
          <ul>
            <li><strong>API:</strong> Duffel API (Test Environment)</li>
            <li><strong>Status:</strong> {searchExecuted ? (flights.length > 0 ? '✅ Working' : '⚠️ No Results') : 'Ready to test'}</li>
            <li><strong>Backend:</strong> Running on http://localhost:3001</li>
            <li><strong>Note:</strong> Duffel generates offers asynchronously, so searches may take 2-5 seconds</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DuffelTestPage;
